import { createUrl, isPaginatedResponse } from "@/utilities/api";
import {
  MinUser,
  ProfileType,
  User,
  UserProfileType,
} from "@/views/profiles/types/user";
import { BuilderType } from "@/types/base";
import {
  ProfileTypeQueryParams,
  UserProfileTypeQueryParams,
  UserQueryParams,
  UserTags,
  UsersTagTypes,
} from "./types";
import { PaginatedResponse } from "@/types/pagination";

const usersService = (builder: BuilderType<UserTags>) => ({
  login: builder.mutation({
    query: (data) => ({
      url: "/auth/login/",
      method: "POST",
      data,
    }),
  }),
  register: builder.mutation({
    query: (data) => ({
      url: "/auth/register/",
      method: "POST",
      data,
    }),
  }),
  resetPassword: builder.mutation({
    query: (data) => ({
      url: "/auth/password/reset/",
      method: "POST",
      data,
    }),
  }),
  resetPasswordConfirm: builder.mutation({
    query: (data) => ({
      url: "/auth/password/reset/confirm/",
      method: "POST",
      data,
    }),
  }),
  verifyEmail: builder.mutation({
    query: (data) => ({
      url: "/auth/email-verification/",
      method: "POST",
      data,
    }),
  }),
  verifyEmailResend: builder.mutation({
    query: (data) => ({
      url: "/auth/email-verification/resend/",
      method: "POST",
      data,
    }),
  }),
  logOut: builder.mutation({
    query: () => ({
      url: "/auth/logout/",
      method: "POST",
    }),
  }),
  getUsers: builder.query<
    MinUser[] | PaginatedResponse<MinUser>,
    UserQueryParams
  >({
    query: (filters) => createUrl("/users/", filters),
    providesTags: (result): UsersTagTypes[] =>
      result
        ? [
            ...(
              (isPaginatedResponse(result)
                ? result.results
                : result) as MinUser[]
            ).map((i) => ({
              type: "User",
              id: i.id,
            })),
            { type: "Users" },
          ]
        : [],
  }),
  getCurrentUser: builder.query<User, void>({
    query: () => "/users/me/",
    providesTags: (result) => (result ? [{ type: "CurrentUser" }] : []),
  }),
  updateCurrentUser: builder.mutation({
    query: (data) => ({
      url: "/users/me/",
      method: "PATCH",
      data,
    }),
    invalidatesTags: (result) =>
      result ? [{ type: "CurrentUser" }, { type: "Users" }] : [],
  }),
  deleteCurrentUser: builder.mutation({
    query: (data) => ({
      url: "/users/me/",
      method: "DELETE",
      data,
    }),
  }),
  getUserById: builder.query<User, string | number>({
    query: (id) => `/users/${id}/`,
    providesTags: (result) =>
      result ? [{ type: "User", id: result.id }, { type: "Users" }] : [],
  }),
  updateUser: builder.mutation<User, { id: number; data: any }>({
    query: ({ id, data }) => ({
      url: `/users/${id}/`,
      method: "PATCH",
      data,
    }),
    invalidatesTags: (result): UsersTagTypes[] =>
      result ? [{ type: "User", id: result.id }, { type: "Users" }] : [],
  }),
  exportUserData: builder.mutation({
    query: (id) => ({
      url: `/users/${id}/export/`,
      method: "POST",
    }),
  }),
  impersonateUser: builder.query({
    query: (id) => ({
      url: `/users/${id}/impersonate/`,
      method: "GET",
    }),
  }),
  getExportedUserData: builder.query({
    query: (id) => `/users/${id}/export/`,
  }),
  followUser: builder.mutation({
    query: (data) => ({
      url: "/followers/",
      method: "POST",
      data,
    }),
    invalidatesTags: (result) =>
      result
        ? [
            {
              type: "User",
              id: result.followed,
            },
          ]
        : [],
  }),
  unfollowUser: builder.mutation({
    query: ({ userFollowerId }) => ({
      url: `/followers/${userFollowerId}/`,
      method: "DELETE",
    }),
    invalidatesTags: (result, error, { userId }) =>
      error
        ? []
        : [
            {
              type: "User",
              id: userId,
            },
          ],
  }),
  getProfileTypes: builder.query<ProfileType[], ProfileTypeQueryParams | void>({
    query: (params) => createUrl("/profile-types/", params),
    providesTags: (result): UsersTagTypes[] =>
      result
        ? [{ type: "ProfileType", id: result.id }, { type: "ProfileTypes" }]
        : [],
  }),
  getUserProfileTypes: builder.query<
    UserProfileType[],
    UserProfileTypeQueryParams | void
  >({
    query: (params) => ({
      url: createUrl("/user-profile-types/", params),
      method: "GET",
    }),
    providesTags: (result): UsersTagTypes[] =>
      result
        ? [
            ...(
              (isPaginatedResponse(result)
                ? result.results
                : result) as UserProfileType[]
            ).map(
              (type): UsersTagTypes => ({
                type: "UserProfileType",
                id: type.id,
              }),
            ),
            { type: "UserProfileTypes" },
          ]
        : [],
  }),
  getUserProfileTypeById: builder.query<
    UserProfileType,
    UserProfileTypeQueryParams | void
  >({
    query: (id) => ({
      url: `/user-profile-types/${id}/`,
      method: "GET",
    }),
    providesTags: (result) =>
      result
        ? [
            { type: "UserProfileType", id: result.id },
            { type: "UserProfileTypes" },
          ]
        : [],
  }),
  createUserProfileType: builder.mutation<UserProfileType, any>({
    query: (data) => ({
      url: "/user-profile-types/",
      method: "POST",
      data,
    }),
    invalidatesTags: (result) =>
      result
        ? [
            {
              type: "UserProfileTypes",
            },
          ]
        : [],
  }),
  updateUserProfileType: builder.mutation<
    UserProfileType,
    { id: number; data: any }
  >({
    query: ({ id, data }) => ({
      url: `/user-profile-types/${id}/`,
      method: "PUT",
      data,
    }),
    invalidatesTags: (result) =>
      result
        ? [
            {
              type: "UserProfileType",
              id: result.id,
            },
          ]
        : [],
  }),
  patchUserProfileType: builder.mutation<
    UserProfileType,
    { id: number; data: any }
  >({
    query: ({ id, data }) => ({
      url: `/user-profile-types/${id}/`,
      method: "PATCH",
      data,
    }),
    invalidatesTags: (result) =>
      result
        ? [
            {
              type: "UserProfileType",
              id: result.id,
            },
          ]
        : [],
  }),
});

export default usersService;
